import {ReactNode, useCallback, useEffect, useState} from "react";
import {AccountRecordingsContext} from "../../contexts/Account/AccountRecordingsContext.tsx";
import {IRecording} from "../../interfaces/account/recording/IRecording.ts";
import apiNet from "../../newApi/apiNet.ts";
import {useNotification} from "../NotificationProvider.tsx";
import axios from "axios";
import {NotificationStatus} from "../../components/notification/Notification.tsx";
import {IRecordingItem} from "../../interfaces/account/recording/IRecordingItem.ts";
import {IRecordingDto} from "../../response/account/getMeResponse.ts";
import {useAuth} from "../../hooks/useAuth.tsx";
import camelcaseKeys from "camelcase-keys";

interface IProps {
    children: ReactNode;
}

const AccountRecordingsProvider = ({children}: IProps) => {
    const {showNotification} = useNotification();
    const [recording, setRecording] = useState<IRecording | null | "loading">("loading");
    const {user} = useAuth();

    const getAll = useCallback(async (): Promise<void> => {
        try {
            const result = await apiNet.getRecordings();
            const recording = {
                maxRecordsLength: result.data.max_records_length,
                freeRecordsLength: result.data.total_records_length,
                recordings: camelcaseKeys(result.data.recordings)
            } as IRecording;
            setRecording(recording);
        } catch (e) {
            if (axios.isAxiosError(e)) {
                const message = e.response?.data?.message ?? "Błąd podczas pobierania nagrań";
                showNotification("Błąd API", message, NotificationStatus.failure);
            } else {
                showNotification("Niepowodzenie", "", NotificationStatus.failure);
            }
        }
    }, [showNotification]);

    const updateRecordingsBasic = useCallback((ids: IRecordingDto[]) => {
        const recordings = ids.map(item => ({id: item.id, programRealId: item.program_real_id} as IRecordingItem));
        setRecording({recordings: recordings} as IRecording);
    }, []);

    const deleteRecording = useCallback(async (id: number): Promise<void> => {
        try {
            await apiNet.deleteRecording(id);

            setRecording(prev => {
                if (!prev || prev == "loading") return null;
                return {
                    ...prev,
                    recordings: prev.recordings.filter(r => r.id !== id),
                };
            });

            showNotification("Sukces", "Nagranie zostało usunięte", NotificationStatus.success);
        } catch (e) {
            if (axios.isAxiosError(e)) {
                const message = e.response?.data?.message ?? "Błąd podczas usuwania nagrania";
                showNotification("Błąd API", message, NotificationStatus.failure);
            } else {
                showNotification("Niepowodzenie", "", NotificationStatus.failure);
            }
        }
    }, [showNotification]);

    const _clearState = () => {
        setRecording(null);
    }

    useEffect(() => {
        if (user == null) {
            _clearState();
        }
    }, [user])

    return (
        <AccountRecordingsContext.Provider
            value={{recordings: recording, getAll, updateRecordingsBasic, delete: deleteRecording}}>
            {children}
        </AccountRecordingsContext.Provider>
    );
};

export default AccountRecordingsProvider;